import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import medicoVfpService, { PacienteVfp } from '../../services/medicoVfpService';
import {
  ArrowLeft as ArrowLeftIcon,
  Save as SaveIcon,
  User as UserIcon,
} from 'lucide-react';

export default function MedicoVfpEditPage() {
  const { clave } = useParams<{ clave: string }>();
  const navigate = useNavigate();
  const [paciente, setPaciente] = useState<PacienteVfp | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [nombre, setNombre] = useState('');
  const [apellido1, setApellido1] = useState('');
  const [apellido2, setApellido2] = useState('');
  const [sexo, setSexo] = useState('');
  const [nacimiento, setNacimiento] = useState('');
  const [peso, setPeso] = useState('');
  const [talla, setTalla] = useState('');
  const [temp, setTemp] = useState('');
  const [ta, setTa] = useState('');
  const [direccion, setDireccion] = useState('');
  const [tel1, setTel1] = useState('');
  const [tel2, setTel2] = useState('');
  const [padre, setPadre] = useState('');
  const [procedencia, setProcedencia] = useState('');

  useEffect(() => {
    if (clave) {
      loadPaciente();
    }
  }, [clave]);

  const loadPaciente = async () => {
    try {
      setLoading(true);
      const data = await medicoVfpService.getPacienteVfp(parseInt(clave!));
      setPaciente(data);

      // Set form values
      setNombre(data.nombre || '');
      setApellido1(data.apellido1 || '');
      setApellido2(data.apellido2 || '');
      setSexo(data.sexo || '');
      // Convertir la fecha al formato YYYY-MM-DD para el input date
      if (data.nacimiento) {
        const fecha = new Date(data.nacimiento);
        setNacimiento(fecha.toISOString().split('T')[0]);
      }
      setPeso(data.peso?.toString() || '');
      setTalla(data.talla?.toString() || '');
      setTemp(data.temp?.toString() || '');
      setTa(data.ta?.toString() || '');
      setDireccion(data.medicoVfpPaciDat?.direccion || '');
      setTel1(data.medicoVfpPaciDat?.tel1 || '');
      setTel2(data.medicoVfpPaciDat?.tel2 || '');
      setPadre(data.medicoVfpPaciDat?.padre || '');
      setProcedencia(data.procedencia || '');
    } catch (error) {
      console.error('Error al cargar paciente:', error);
      toast.error('Error al cargar paciente');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!apellido1.trim()) {
      toast.error('El apellido 1 es obligatorio');
      return;
    }

    if (!nombre.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }

    if (!sexo) {
      toast.error('El sexo es obligatorio');
      return;
    }

    try {
      setSaving(true);
      await medicoVfpService.updatePacienteVfp(parseInt(clave!), {
        nombre: nombre.trim(),
        apellido1: apellido1.trim(),
        apellido2: apellido2.trim() || undefined,
        sexo: sexo || undefined,
        nacimiento: nacimiento || undefined,
        peso: peso ? parseFloat(peso) : undefined,
        talla: talla ? parseFloat(talla) : undefined,
        temp: temp ? parseFloat(temp) : undefined,
        ta: ta ? parseFloat(ta) : undefined,
        direccion: direccion.trim() || undefined,
        tel1: tel1.trim() || undefined,
        tel2: tel2.trim() || undefined,
        padre: padre.trim() || undefined,
        procedencia: procedencia || undefined,
      });

      toast.success('Paciente actualizado exitosamente');
      navigate('/medico-vfp/pacientes');
    } catch (error) {
      console.error('Error al actualizar paciente:', error);
      toast.error('Error al actualizar paciente');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!paciente) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Paciente no encontrado</p>
          <button
            onClick={() => navigate('/medico-vfp/pacientes')}
            className="text-purple-600 hover:text-purple-800"
          >
            ← Volver a Pacientes VFP
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/medico-vfp/pacientes')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
              >
                <ArrowLeftIcon className="w-5 h-5" />
                Volver
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Editar Paciente VFP</h1>
                <p className="text-gray-600">CLAVE: {paciente.clave}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <UserIcon className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Información del Paciente</h2>
                <p className="text-sm text-gray-600">Modifique los datos que necesite actualizar</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Nombres */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Apellido 1 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={apellido1}
                  onChange={(e) => setApellido1(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Primer apellido"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Apellido 2
                </label>
                <input
                  type="text"
                  value={apellido2}
                  onChange={(e) => setApellido2(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Segundo apellido"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Nombre(s)"
                  required
                />
              </div>
            </div>

            {/* Sexo, Fecha de Nacimiento y Procedencia */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sexo <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-6">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="sexo"
                      value="H"
                      checked={sexo === 'H'}
                      onChange={(e) => setSexo(e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-gray-700">Hombre</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="sexo"
                      value="M"
                      checked={sexo === 'M'}
                      onChange={(e) => setSexo(e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-gray-700">Mujer</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha de Nacimiento
                </label>
                <input
                  type="date"
                  value={nacimiento}
                  onChange={(e) => setNacimiento(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Procedencia
                </label>
                <select
                  value={procedencia}
                  onChange={(e) => setProcedencia(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Sin asignar</option>
                  <option value="beneficencia">Beneficencia Española</option>
                  <option value="angeles">Hospital Angeles</option>
                </select>
              </div>
            </div>

            {/* Datos médicos */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-4">Datos Médicos</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Peso (kg)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={peso}
                    onChange={(e) => setPeso(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Ej: 65.5"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Talla (m)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={talla}
                    onChange={(e) => setTalla(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Ej: 1.65"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Temperatura</label>
                  <input
                    type="number"
                    step="0.1"
                    value={temp}
                    onChange={(e) => setTemp(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Ej: 36.5"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tensión Arterial</label>
                  <input
                    type="number"
                    step="0.1"
                    value={ta}
                    onChange={(e) => setTa(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Ej: 120.0"
                  />
                </div>
              </div>
            </div>

            {/* Datos de contacto */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-4">Datos de Contacto</h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Dirección</label>
                  <textarea
                    value={direccion}
                    onChange={(e) => setDireccion(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Dirección completa"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono 1</label>
                    <input
                      type="text"
                      value={tel1}
                      onChange={(e) => setTel1(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Teléfono principal"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono 2</label>
                    <input
                      type="text"
                      value={tel2}
                      onChange={(e) => setTel2(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Teléfono secundario"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Padre/Tutor</label>
                    <input
                      type="text"
                      value={padre}
                      onChange={(e) => setPadre(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Nombre del padre o tutor"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3 rounded-b-lg">
            <button
              type="button"
              onClick={() => navigate('/medico-vfp/pacientes')}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              <SaveIcon className="w-5 h-5" />
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
